import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Trophy, TrendingUp, User, Star, Download, Shield } from 'lucide-react';
import { cn } from '../lib/utils';
import api, { fetchGames } from '../lib/api';
import { buildPitcherDailyLoad } from '../lib/pitcherRest';

const StatBox = ({ label, value, highlight = false }) => (
  <div className={cn('text-center p-2 rounded-lg', highlight ? 'bg-green-900/40 border border-green-700/50' : 'bg-stone-700/50')}>
    <p className={cn('text-2xl font-extrabold', highlight ? 'text-green-400' : 'text-white')} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
      {value}
    </p>
    <p className="text-[10px] uppercase tracking-wider text-stone-400">{label}</p>
  </div>
);

const ymdEq = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export default function PlayerProfilePage() {
  const navigate = useNavigate();
  const { teamId, playerId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dailyLoad, setDailyLoad] = useState([]);

  useEffect(() => {
    if (!teamId || !playerId) { navigate('/'); return; }
    loadData();
  }, [teamId, playerId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/stats/player/${teamId}/${playerId}`);
      setData(res.data);
      // If this player is a pitcher, fetch games and build 14-day workload
      if (res.data?.player?.is_pitcher) {
        try {
          const games = await fetchGames(teamId);
          setDailyLoad(buildPitcherDailyLoad(games || [], playerId, 14));
        } catch { setDailyLoad([]); }
      } else {
        setDailyLoad([]);
      }
    } catch (error) {
      console.error('Error loading player stats:', error);
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900">
        <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-900 text-white gap-4">
        <p>Player not found</p>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  const { player, team_name, career, game_logs, season_splits } = data;

  return (
    <div className="min-h-screen bg-stone-900 p-4 md:p-6" data-testid="player-profile-page">
      <div className="max-w-5xl mx-auto space-y-4">

        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" className="text-stone-400 hover:text-white" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={16} />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              PLAYER PROFILE
            </h1>
            <p className="text-stone-500 text-xs">{team_name}</p>
          </div>
          <Button size="sm" variant="outline" className="text-stone-300 border-stone-600 hover:bg-stone-700"
            data-testid="export-player-csv-btn"
            onClick={() => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/player-stats/${teamId}/${playerId}`;
              const a = document.createElement('a'); a.href = url; a.download = 'player_stats.csv'; a.click();
            }}>
            <Download size={14} className="mr-1" /> CSV
          </Button>
        </div>

        {/* Player Card */}
        <Card className="border-0 bg-gradient-to-r from-green-900/60 to-stone-800 shadow-xl overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 rounded-xl bg-white text-green-900 flex items-center justify-center text-4xl font-extrabold shadow-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {player.jersey_number || '?'}
              </div>
              <div className="flex-1">
                <h2 className="text-3xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="player-name">
                  {player.name}
                </h2>
                <div className="flex items-center gap-3 mt-1">
                  {player.position && (
                    <span className="bg-stone-700 text-stone-300 px-2 py-0.5 rounded text-xs font-semibold">{player.position}</span>
                  )}
                  {player.is_pitcher && (
                    <span className="bg-amber-600 text-white px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1">
                      <Star size={10} /> Pitcher
                    </span>
                  )}
                  <span className="text-stone-400 text-xs">{career.games} game{career.games !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rest Calendar (pitchers only) */}
        {player.is_pitcher && (
          <Card className="border-0 bg-stone-800 shadow-xl" data-testid="rest-calendar-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <Shield size={16} className="text-amber-400" /> REST CALENDAR · LAST 14 DAYS
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const totalPitches = dailyLoad.reduce((a, b) => a + b.pitches, 0);
                const outings = dailyLoad.filter(d => d.pitches > 0).length;
                const heaviest = dailyLoad.reduce((m, d) => d.pitches > m ? d.pitches : m, 0);
                const tierStyle = {
                  none: 'bg-stone-700/40 text-stone-600 border-stone-700/40',
                  'rest-light': 'bg-green-900/40 text-green-300 border-green-700/60',
                  low: 'bg-green-700/60 text-white border-green-600',
                  moderate: 'bg-amber-600/70 text-white border-amber-500',
                  heavy: 'bg-orange-600/80 text-white border-orange-500',
                  max: 'bg-red-600/90 text-white border-red-500',
                };
                const dayOfWeek = (d) => ['S','M','T','W','T','F','S'][d.getDay()];
                const todayKey = new Date(); todayKey.setHours(0,0,0,0);
                return (
                  <>
                    <div className="flex items-center gap-2 text-[10px] text-stone-400 mb-3">
                      <span><span className="text-white font-bold">{totalPitches}</span> pitches</span>
                      <span>·</span>
                      <span><span className="text-white font-bold">{outings}</span> outing{outings === 1 ? '' : 's'}</span>
                      <span>·</span>
                      <span>heaviest <span className="text-white font-bold">{heaviest}</span></span>
                    </div>
                    <div className="grid grid-cols-7 sm:grid-cols-14 gap-1.5" data-testid="rest-calendar-grid">
                      {dailyLoad.map((d, idx) => {
                        const isToday = ymdEq(d.date, todayKey);
                        return (
                          <div
                            key={d.dateKey}
                            data-testid={`rest-cal-day-${idx}`}
                            title={`${d.dateKey}: ${d.pitches} pitches${d.restRequired ? ` · ${d.restRequired} rest day${d.restRequired === 1 ? '' : 's'} required` : ''}`}
                            className={cn(
                              'aspect-square rounded-md border flex flex-col items-center justify-center p-1 transition-all',
                              tierStyle[d.tier],
                              isToday && 'ring-2 ring-amber-400'
                            )}
                          >
                            <span className="text-[8px] uppercase opacity-70 leading-none">{dayOfWeek(d.date)}</span>
                            <span className="text-sm font-extrabold leading-none mt-0.5" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                              {d.pitches > 0 ? d.pitches : '—'}
                            </span>
                            <span className="text-[8px] opacity-60 leading-none mt-0.5">{d.date.getDate()}</span>
                          </div>
                        );
                      })}
                    </div>
                    {/* Legend */}
                    <div className="flex flex-wrap items-center gap-2 mt-3 text-[9px] text-stone-400">
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-stone-700/60 border border-stone-700/40" /> Rest</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-900/40 border border-green-700/60" /> 1-20</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-700/60" /> 21-35</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-600/70" /> 36-50</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-orange-600/80" /> 51-65</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-600/90" /> 66+ (max)</span>
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Career Stats */}
        <Card className="border-0 bg-stone-800 shadow-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <TrendingUp size={16} className="text-green-400" /> CAREER STATS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-2" data-testid="career-stats">
              <StatBox label="G" value={career.games} />
              <StatBox label="AB" value={career.ab} />
              <StatBox label="H" value={career.h} highlight={career.h > 0} />
              <StatBox label="1B" value={career.singles} />
              <StatBox label="2B" value={career.doubles} />
              <StatBox label="3B" value={career.triples} />
              <StatBox label="HR" value={career.hr} highlight={career.hr > 0} />
              <StatBox label="RBI" value={career.rbi} highlight={career.rbi > 0} />
              <StatBox label="BB" value={career.bb} />
              <StatBox label="K" value={career.k} />
              <StatBox label="AVG" value={career.avg.toFixed(3)} highlight={career.avg >= 0.300} />
              <StatBox label="OPS" value={career.ops.toFixed(3)} highlight={career.ops >= 0.800} />
            </div>
          </CardContent>
        </Card>

        {/* Season Splits */}
        {season_splits.length > 0 && (
          <Card className="border-0 bg-stone-800 shadow-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                SEASON-BY-SEASON
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="season-splits-table">
                <thead>
                  <tr className="border-b border-stone-700 text-stone-400">
                    <th className="text-left py-2 px-2">Season</th>
                    <th className="text-center py-2 px-1">G</th>
                    <th className="text-center py-2 px-1">AB</th>
                    <th className="text-center py-2 px-1">H</th>
                    <th className="text-center py-2 px-1">HR</th>
                    <th className="text-center py-2 px-1">RBI</th>
                    <th className="text-center py-2 px-1">BB</th>
                    <th className="text-center py-2 px-1">K</th>
                    <th className="text-center py-2 px-1">AVG</th>
                    <th className="text-center py-2 px-1">OBP</th>
                    <th className="text-center py-2 px-1">SLG</th>
                    <th className="text-center py-2 px-1">OPS</th>
                  </tr>
                </thead>
                <tbody>
                  {season_splits.map((s) => (
                    <tr key={s.season} className="border-b border-stone-700/30 hover:bg-stone-700/30">
                      <td className="text-stone-200 py-2 px-2 font-semibold">{s.season}</td>
                      <td className="text-center text-stone-300 py-2 px-1">{s.games}</td>
                      <td className="text-center text-stone-300 py-2 px-1">{s.ab}</td>
                      <td className={cn('text-center py-2 px-1', s.h > 0 ? 'text-green-400 font-bold' : 'text-stone-500')}>{s.h}</td>
                      <td className={cn('text-center py-2 px-1', s.hr > 0 ? 'text-amber-400 font-bold' : 'text-stone-500')}>{s.hr || '-'}</td>
                      <td className={cn('text-center py-2 px-1', s.rbi > 0 ? 'text-green-300 font-bold' : 'text-stone-500')}>{s.rbi || '-'}</td>
                      <td className="text-center text-stone-400 py-2 px-1">{s.bb || '-'}</td>
                      <td className="text-center text-stone-400 py-2 px-1">{s.k || '-'}</td>
                      <td className={cn('text-center py-2 px-1 font-mono', s.avg >= 0.300 ? 'text-green-400 font-bold' : 'text-stone-300')}>{s.avg.toFixed(3)}</td>
                      <td className="text-center text-stone-400 py-2 px-1 font-mono">{s.obp.toFixed(3)}</td>
                      <td className="text-center text-stone-400 py-2 px-1 font-mono">{s.slg.toFixed(3)}</td>
                      <td className={cn('text-center py-2 px-1 font-mono', s.ops >= 0.800 ? 'text-amber-400 font-bold' : 'text-stone-300')}>{s.ops.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Game-by-Game Log */}
        <Card className="border-0 bg-stone-800 shadow-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Trophy size={16} className="text-amber-400" /> GAME LOG ({game_logs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {game_logs.length === 0 ? (
              <p className="text-stone-500 text-center py-6 text-sm">No game data yet</p>
            ) : (
              <table className="w-full text-xs" data-testid="game-log-table">
                <thead>
                  <tr className="border-b border-stone-700 text-stone-400">
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-left py-2 px-2">Opponent</th>
                    <th className="text-center py-2 px-1">AB</th>
                    <th className="text-center py-2 px-1">H</th>
                    <th className="text-center py-2 px-1">1B</th>
                    <th className="text-center py-2 px-1">2B</th>
                    <th className="text-center py-2 px-1">3B</th>
                    <th className="text-center py-2 px-1">HR</th>
                    <th className="text-center py-2 px-1">RBI</th>
                    <th className="text-center py-2 px-1">BB</th>
                    <th className="text-center py-2 px-1">K</th>
                    <th className="text-center py-2 px-1">AVG</th>
                  </tr>
                </thead>
                <tbody>
                  {game_logs.map((g) => (
                    <tr key={g.game_id}
                      className="border-b border-stone-700/30 hover:bg-stone-700/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/game-summary/${teamId}/${g.game_id}`)}
                    >
                      <td className="text-stone-400 py-2 px-2">{g.date}</td>
                      <td className="text-stone-200 py-2 px-2 font-medium">vs {g.opponent}</td>
                      <td className="text-center text-stone-300 py-2 px-1">{g.ab}</td>
                      <td className={cn('text-center py-2 px-1', g.h > 0 ? 'text-green-400 font-bold' : 'text-stone-500')}>{g.h}</td>
                      <td className="text-center text-stone-400 py-2 px-1">{g.singles || '-'}</td>
                      <td className="text-center text-stone-400 py-2 px-1">{g.doubles || '-'}</td>
                      <td className="text-center text-stone-400 py-2 px-1">{g.triples || '-'}</td>
                      <td className={cn('text-center py-2 px-1', g.hr > 0 ? 'text-amber-400 font-bold' : 'text-stone-500')}>{g.hr || '-'}</td>
                      <td className={cn('text-center py-2 px-1', g.rbi > 0 ? 'text-green-300 font-bold' : 'text-stone-500')}>{g.rbi || '-'}</td>
                      <td className="text-center text-stone-400 py-2 px-1">{g.bb || '-'}</td>
                      <td className="text-center text-stone-400 py-2 px-1">{g.k || '-'}</td>
                      <td className={cn('text-center py-2 px-1 font-mono', g.avg >= 0.300 ? 'text-green-400 font-bold' : 'text-stone-300')}>{g.avg.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
